"use client";

import { JSX, useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import { SEGMENT_ANONYMOUS_ID_COOKIE } from "@/constants/cookies";
import { readCookie, setCookie } from "@/utils/cookies";
import { registerGa4ColdPageViewHook } from "@/utils/sendGA4PageView";

// 1. Define the allowed values as "const" arrays
const BUSINESS_LINES = ["hair", "skin", "company"] as const;
const PAGE_CATEGORIES = ["men", "women", "brand"] as const;
const PAGE_TYPES = ["product", "lp", "quiz", "partner"] as const;
const PAGE_DETAILS = ["core", "scalp", "kit", "booster", "quiz", "category"] as const;
const PRODUCT_LINE = ["men", "women", "neutral"] as const;

// 2. Derive types from the arrays automatically
type BusinessLine = (typeof BUSINESS_LINES)[number];
type PageCategory = (typeof PAGE_CATEGORIES)[number];
type PageType = (typeof PAGE_TYPES)[number];
type PageDetail = (typeof PAGE_DETAILS)[number];
type PageLine = (typeof PRODUCT_LINE)[number];

interface PageEventProps {
  business_line: BusinessLine;
  page_category: PageCategory;
  page_type: PageType;
  page_product_line: PageLine;
  page_detail: PageDetail | null;
  page_referrer: string;
  page_template: string;
  page_intent: string;
  path: string;
  url: string;
  userAgent: string;
  title: string;
}
declare global {
  interface Window {
    analytics?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      page: (title: string, options?: Record<string, any>) => void | Promise<any>;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      track: (event: string, props?: Record<string, any>) => void | Promise<any>;
      identify: (
        userIdOrTraits: string | Record<string, unknown>,
        props?: Record<string, unknown>
      ) => void;
      ready?: (callback: () => void) => void;
      user?: () => { anonymousId: () => string };
      setAnonymousId?: (id: string) => void;
    };
  }
}

function setSegmentAnonymousIdCookieOnce(): void {
  if (readCookie(SEGMENT_ANONYMOUS_ID_COOKIE)) return;
  const a = window.analytics;
  if (!a?.ready) return;
  a.ready(() => {
    if (readCookie(SEGMENT_ANONYMOUS_ID_COOKIE)) return;
    try {
      const an = typeof window !== "undefined" ? window.analytics : undefined;
      const id = an?.user?.().anonymousId?.();
      if (id) setCookie(SEGMENT_ANONYMOUS_ID_COOKIE, id, 30);
    } catch {
      // ignore
    }
  });
}

export const getSegmentAnonymousId = () => readCookie(SEGMENT_ANONYMOUS_ID_COOKIE);

export function AnalyticsTracker(): JSX.Element {
  const pathname = usePathname();
  const prevPathRef = useRef<string | null>(null);

  /*
   * Cold-load page_view fires in gtag config snippet (after Segment pre-seed).
   * Hook allows OptanonWrapper to send if head snippet did not.
   */
  useEffect(() => {
    registerGa4ColdPageViewHook();
    setSegmentAnonymousIdCookieOnce();
  }, []);

  /*
   * Segment SPA page calls only — not GA4.
   */
  useEffect(() => {
    if (typeof window !== "undefined" && window.analytics) {
      const pageTracker = () => {
        const name = document.title.split(" | ")[0] || "Home";

        // Referrer Logic
        const page_referrer = prevPathRef.current
          ? window.location.origin + prevPathRef.current
          : document.referrer;

        // Path Parsing logic
        const pathParts = window.location.pathname.split("/").filter(Boolean);
        const [raw_business, raw_category, raw_type, raw_detail] = pathParts;

        const eventData: PageEventProps = {
          // Use a helper function or direct "includes" check
          business_line: BUSINESS_LINES.includes(raw_business as BusinessLine)
            ? (raw_business as BusinessLine)
            : "hair",

          page_category: PAGE_CATEGORIES.includes(raw_category as PageCategory)
            ? (raw_category as PageCategory)
            : "women",

          page_type: PAGE_TYPES.includes(raw_type as PageType) ? (raw_type as PageType) : "product",

          page_product_line: PRODUCT_LINE.includes(raw_category as PageLine)
            ? (raw_category as PageLine)
            : "neutral",

          page_detail: PAGE_DETAILS.includes(raw_detail as PageDetail)
            ? (raw_detail as PageDetail)
            : null,
          page_referrer,
          page_template: "default",
          page_intent: "browse",
          path: window.location.pathname,
          url: window.location.href,
          userAgent: navigator.userAgent,
          title: name,
        };

        window.analytics?.page(name, eventData);

        //console.log("Page Viewed", eventData);

        // Update Ref for next navigation
        prevPathRef.current = window.location.pathname;
      };

      const timer = setTimeout(pageTracker, 250);
      return () => clearTimeout(timer);
    } else {
      /*
       *  Using warn instead of error to avoid cluttering logs if script is slow to load
       * console.warn("window.analytics not yet available");
       */
    }
  }, [pathname]);

  return null;
}
